import { call, put, takeLatest, delay, race, take, select } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import vneidAccountService from '../../../services/vneidAccountService';

import {
  fetchSSOConfig,
  fetchSSOConfigSuccess,
  fetchSSOConfigFailure,
  saveSSOConfig,
  saveSSOConfigSuccess,
  saveSSOConfigFailure,
  pingConnection,
  pingConnectionSuccess,
  pingConnectionFailure,
  refreshToken,
  refreshTokenSuccess,
  refreshTokenFailure,
  startMonitoring,
  stopMonitoring,
  updateConnectionStatus,
  checkInterruptions,
  checkInterruptionsSuccess,
  checkInterruptionsFailure,
  fetchRecentEvents,
  fetchRecentEventsSuccess,
  fetchRecentEventsFailure,
  fetchAccounts,
  fetchAccountsSuccess,
  fetchAccountsFailure,
  fetchAccountById,
  fetchAccountByIdSuccess,
  fetchAccountByIdFailure,
  fetchStatistics,
  fetchStatisticsSuccess,
  fetchStatisticsFailure,
  createAccount,
  createAccountSuccess,
  createAccountFailure,
  updateAccount,
  updateAccountSuccess,
  updateAccountFailure,
  deleteAccount,
  deleteAccountSuccess,
  deleteAccountFailure,
  bulkDeleteAccounts,
  bulkDeleteAccountsSuccess,
  bulkDeleteAccountsFailure,
  toggleAccountStatus,
  toggleAccountStatusSuccess,
  toggleAccountStatusFailure,
  regenerateApiKey,
  regenerateApiKeySuccess,
  regenerateApiKeyFailure,
  revokeApiKey,
  revokeApiKeySuccess,
  revokeApiKeyFailure,
  assignSdk,
  assignSdkSuccess,
  assignSdkFailure
} from './slice';

// Fetch SSO Configuration from Settings API
function* fetchSSOConfigSaga() {
  try {
    const response = yield call(api.get, `${config.endpoints.settings.list}`);

    // Find VNeID integration category
    const vneidCategory = response.data.find(cat => cat.category === 'vneid_integration_category');

    if (vneidCategory) {
      // Map settings to ssoConfig structure
      const ssoConfig = {};
      vneidCategory.settings.forEach(setting => {
        const key = setting.key.replace('vneid_', '');
        // Convert snake_case to camelCase
        const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        ssoConfig[camelKey] = setting.value;
      });

      yield put(fetchSSOConfigSuccess(ssoConfig));
    } else {
      yield put(fetchSSOConfigFailure('VNeID configuration not found'));
    }
  } catch (error) {
    yield put(fetchSSOConfigFailure(error.message));
  }
}

// Save SSO Configuration
function* saveSSOConfigSaga(action) {
  try {
    const { ssoConfig } = action.payload;

    // Convert camelCase to snake_case and prepend vneid_
    const settings = Object.entries(ssoConfig).map(([key, value]) => {
      const snakeKey = 'vneid_' + key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      return {
        key: snakeKey,
        value: value
      };
    });

    yield call(api.put, `${config.endpoints.settings.update}`, { settings });
    yield put(saveSSOConfigSuccess('Cấu hình đã được lưu thành công'));
  } catch (error) {
    yield put(saveSSOConfigFailure(error.message));
  }
}

// Ping VNeID Connection
function* pingConnectionSaga() {
  try {
    // Call connection status endpoint to check connection
    const response = yield call(api.get, config.endpoints.vneidQueryMonitoring.connectionStatus);

    // Update connection status
    yield put(updateConnectionStatus({
      status: response.status || 'OFFLINE',
      lastChecked: response.lastChecked || new Date().toISOString(),
      avgResponseTime: response.avgResponseTime || 0,
      uptime: response.uptime || 0,
      downtimeMinutes: response.downtimeMinutes || 0,
      recentRequests: response.recentRequests || 0
    }));

    yield put(pingConnectionSuccess({
      message: response.status === 'ONLINE' ? 'Kết nối thành công' : 'Kết nối không ổn định',
      responseTime: response.avgResponseTime || 0
    }));
  } catch (error) {
    yield put(updateConnectionStatus({
      status: 'OFFLINE',
      lastChecked: new Date().toISOString()
    }));
    yield put(pingConnectionFailure('Không thể kết nối đến VNeID'));
  }
}

// Refresh Authentication Token
function* refreshTokenSaga() {
  try {
    // Call refresh token endpoint (placeholder - replace with actual endpoint)
    yield call(api.post, `${config.endpoints.vneidIntegration.refreshToken || '/api/vneid/refresh-token'}`);

    yield put(refreshTokenSuccess('Token đã được làm mới thành công'));
  } catch (error) {
    yield put(refreshTokenFailure(error.message));
  }
}

// Monitor Connection Status
function* monitorConnectionSaga() {
  while (true) {
    try {
      const { cancel } = yield race({
        monitoring: call(checkConnectionStatus),
        cancel: take(stopMonitoring.type)
      });

      if (cancel) {
        break;
      }

      yield delay(30000); // Check every 30 seconds
    } catch (error) {
      console.error('Monitoring error:', error);
      yield delay(30000);
    }
  }
}

function* checkConnectionStatus() {
  try {
    // Call connection status endpoint
    const response = yield call(api.get, config.endpoints.vneidQueryMonitoring.connectionStatus);

    yield put(updateConnectionStatus({
      status: response.status || 'OFFLINE',
      lastChecked: response.lastChecked || new Date().toISOString(),
      avgResponseTime: response.avgResponseTime || 0,
      uptime: response.uptime || 0,
      downtimeMinutes: response.downtimeMinutes || 0,
      recentRequests: response.recentRequests || 0
    }));
  } catch (error) {
    yield put(updateConnectionStatus({
      status: 'OFFLINE',
      lastChecked: new Date().toISOString()
    }));
  }
}

// Check Interruptions
function* checkInterruptionsSaga() {
  try {
    // Call interruptions endpoint (placeholder - replace with actual endpoint)
    const response = yield call(api.get, `${config.endpoints.vneidIntegration.interruptions || '/api/vneid/interruptions'}`);

    yield put(checkInterruptionsSuccess(response.data));
  } catch (error) {
    yield put(checkInterruptionsFailure(error.message));
  }
}

// Fetch Recent Events
function* fetchRecentEventsSaga() {
  try {
    // Call events endpoint (placeholder - replace with actual endpoint)
    const response = yield call(api.get, `${config.endpoints.vneidIntegration.events || '/api/vneid/events'}`);

    yield put(fetchRecentEventsSuccess(response.data));
  } catch (error) {
    yield put(fetchRecentEventsFailure(error.message));
  }
}

// ========== VNeID Accounts Management Sagas ==========

// Fetch Accounts with pagination, sorting, and filtering
function* fetchAccountsSaga() {
  try {
    const state = yield select();
    const { page, pageSize, sortColumn, sortDirection, filters } = state.vneidStatistics.accounts;

    const params = {
      page,
      pageSize,
      sortColumn,
      sortDirection,
      ...filters
    };

    const response = yield call(vneidAccountService.getAccounts, params);
    yield put(fetchAccountsSuccess(response));
  } catch (error) {
    yield put(fetchAccountsFailure(error.message));
  }
}

// Fetch Account by ID
function* fetchAccountByIdSaga(action) {
  try {
    const response = yield call(vneidAccountService.getAccountById, action.payload);
    yield put(fetchAccountByIdSuccess(response));
  } catch (error) {
    yield put(fetchAccountByIdFailure(error.message));
  }
}

// Fetch Statistics
function* fetchStatisticsSaga() {
  try {
    const response = yield call(vneidAccountService.getStatistics);
    yield put(fetchStatisticsSuccess(response));
  } catch (error) {
    yield put(fetchStatisticsFailure(error.message));
  }
}

// Create Account
function* createAccountSaga(action) {
  try {
    const response = yield call(vneidAccountService.createAccount, action.payload);
    yield put(createAccountSuccess(response));
    // Refresh accounts list and statistics after creation
    yield put(fetchAccounts());
    yield put(fetchStatistics());
  } catch (error) {
    yield put(createAccountFailure(error.message));
  }
}

// Update Account
function* updateAccountSaga(action) {
  try {
    const { id } = action.payload;
    // Keep id in accountData as backend requires it in request body
    const response = yield call(vneidAccountService.updateAccount, id, action.payload);
    yield put(updateAccountSuccess(response));
    // Refresh accounts list and statistics after update
    yield put(fetchAccounts());
    yield put(fetchStatistics());
  } catch (error) {
    yield put(updateAccountFailure(error.message));
  }
}

// Delete Account
function* deleteAccountSaga(action) {
  try {
    const response = yield call(vneidAccountService.deleteAccount, action.payload);
    yield put(deleteAccountSuccess(response));
    // Refresh accounts list and statistics after deletion
    yield put(fetchAccounts());
    yield put(fetchStatistics());
  } catch (error) {
    yield put(deleteAccountFailure(error.message));
  }
}

// Bulk Delete Accounts
function* bulkDeleteAccountsSaga(action) {
  try {
    const response = yield call(vneidAccountService.bulkDeleteAccounts, action.payload);
    yield put(bulkDeleteAccountsSuccess(response));
    // Refresh accounts list and statistics after bulk deletion
    yield put(fetchAccounts());
    yield put(fetchStatistics());
  } catch (error) {
    yield put(bulkDeleteAccountsFailure(error.message));
  }
}

// Toggle Account Status
function* toggleAccountStatusSaga(action) {
  try {
    const response = yield call(vneidAccountService.toggleAccountStatus, action.payload);
    yield put(toggleAccountStatusSuccess(response));
    // Refresh accounts list and statistics after status toggle
    yield put(fetchAccounts());
    yield put(fetchStatistics());
  } catch (error) {
    yield put(toggleAccountStatusFailure(error.message));
  }
}

// Regenerate API Key
function* regenerateApiKeySaga(action) {
  try {
    const response = yield call(vneidAccountService.regenerateApiKey, action.payload);
    yield put(regenerateApiKeySuccess(response));
    // Refresh accounts list to show new API key
    yield put(fetchAccounts());
  } catch (error) {
    yield put(regenerateApiKeyFailure(error.message));
  }
}

// Revoke API Key
function* revokeApiKeySaga(action) {
  try {
    const response = yield call(vneidAccountService.revokeApiKey, action.payload);
    yield put(revokeApiKeySuccess(response));
    // Refresh accounts list
    yield put(fetchAccounts());
  } catch (error) {
    yield put(revokeApiKeyFailure(error.message));
  }
}

// Assign SDK to Account
function* assignSdkSaga(action) {
  try {
    const { accountId, sdkIds } = action.payload;
    const response = yield call(vneidAccountService.assignSdkToAccount, accountId, sdkIds);
    yield put(assignSdkSuccess(response));
    // Refresh accounts list
    yield put(fetchAccounts());
  } catch (error) {
    yield put(assignSdkFailure(error.message));
  }
}

export default function* vneidStatisticsSaga() {
  yield takeLatest(fetchSSOConfig.type, fetchSSOConfigSaga);
  yield takeLatest(saveSSOConfig.type, saveSSOConfigSaga);
  yield takeLatest(pingConnection.type, pingConnectionSaga);
  yield takeLatest(refreshToken.type, refreshTokenSaga);
  yield takeLatest(startMonitoring.type, monitorConnectionSaga);
  yield takeLatest(checkInterruptions.type, checkInterruptionsSaga);
  yield takeLatest(fetchRecentEvents.type, fetchRecentEventsSaga);
  // VNeID Accounts Management
  yield takeLatest(fetchAccounts.type, fetchAccountsSaga);
  yield takeLatest(fetchAccountById.type, fetchAccountByIdSaga);
  yield takeLatest(fetchStatistics.type, fetchStatisticsSaga);
  yield takeLatest(createAccount.type, createAccountSaga);
  yield takeLatest(updateAccount.type, updateAccountSaga);
  yield takeLatest(deleteAccount.type, deleteAccountSaga);
  yield takeLatest(bulkDeleteAccounts.type, bulkDeleteAccountsSaga);
  yield takeLatest(toggleAccountStatus.type, toggleAccountStatusSaga);
  yield takeLatest(regenerateApiKey.type, regenerateApiKeySaga);
  yield takeLatest(revokeApiKey.type, revokeApiKeySaga);
  yield takeLatest(assignSdk.type, assignSdkSaga);
}
